import { Component } from '@theme/component';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { isValidEmail, subscribeToList, parseConfig, getSelectedLists } from '@theme/klaviyo-newsletter-api';

const STORAGE_DISMISSED_KEY = 'klaviyo-newsletter-modal-dismissed';

/**
 * Klaviyo newsletter modal component.
 *
 * @typedef {object} KlaviyoNewsletterModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} formContainer - The form container element.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLInputElement} [listACheckbox] - The first list checkbox.
 * @property {HTMLInputElement} [listBCheckbox] - The second list checkbox.
 * @property {HTMLInputElement} [disclaimerCheckbox] - The disclaimer checkbox.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} submitText - The submit button text.
 * @property {HTMLElement} loadingIndicator - The loading indicator.
 * @property {HTMLElement} successMessage - The success message container.
 * @property {HTMLElement} errorMessage - The error message element.
 *
 * @extends Component<KlaviyoNewsletterModalRefs>
 */
class KlaviyoNewsletterModal extends Component {
  requiredRefs = ['dialog', 'formContainer', 'emailInput', 'submitButton', 'errorMessage'];

  #previousScrollY = 0;
  #showTimeout = null;
  #config = null;

  connectedCallback() {
    super.connectedCallback();

    this.#config = parseConfig(this);

    // Force open immediately when toggled in theme settings
    if (this.#config.forceOpen) {
      this.#prefillEmail();
      this.showDialog();
      return;
    }

    // Don't auto-show when suppressed via modal settings
    if (this.#config.suppress) {
      return;
    }

    // Don't show if recently dismissed
    if (this.#hasBeenDismissed()) {
      return;
    }

    // Schedule the modal to appear after delay
    const delay = this.delaySeconds * 1000;
    this.#showTimeout = setTimeout(() => {
      this.#prefillEmail();
      this.showDialog();
    }, delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
    }
  }

  /**
   * Gets the Klaviyo Company ID from the config.
   * @returns {string}
   */
  get companyId() {
    return this.#config.companyId || '';
  }

  /**
   * Gets the delay in seconds before showing the modal.
   * @returns {number}
   */
  get delaySeconds() {
    return this.#config.delay || 5;
  }

  /**
   * Gets the number of days before showing the modal again after dismissal.
   * @returns {number}
   */
  get dismissDays() {
    return this.#config.dismissDays || 7;
  }

  /**
   * Gets whether to lock scroll when the modal is open.
   * @returns {boolean}
   */
  get lockScroll() {
    return this.#config.lockScroll !== false;
  }

  /**
   * Gets the first list ID from the config.
   * @returns {string}
   */
  get listAId() {
    return this.#config.listAId || '';
  }

  /**
   * Gets the second list ID from the config.
   * @returns {string}
   */
  get listBId() {
    return this.#config.listBId || '';
  }

  /**
   * Gets the locale data for Klaviyo profile properties.
   * @returns {{ locale: string, language: string, country: string, market: string } | null}
   */
  get localeData() {
    if (!this.#config.locale) return null;
    return {
      locale: this.#config.locale,
      language: this.#config.localeLanguage || '',
      country: this.#config.localeCountry || '',
      market: this.#config.market || '',
    };
  }

  /**
   * Checks if the user is logged in.
   * @returns {boolean}
   */
  get isLoggedIn() {
    return Boolean(this.customerEmail);
  }

  /**
   * Gets the logged-in customer's email.
   * @returns {string|null}
   */
  get customerEmail() {
    return this.#config.customerEmail || null;
  }

  /**
   * Gets the email error message from the config.
   * @returns {string}
   */
  get errorEmailMessage() {
    return this.#config.errors?.email || 'Please enter a valid email address';
  }

  /**
   * Gets the list selection error message from the config.
   * @returns {string}
   */
  get errorListMessage() {
    return this.#config.errors?.list || 'Please select at least one email list';
  }

  /**
   * Gets the API error message from the config.
   * @returns {string}
   */
  get errorApiMessage() {
    return this.#config.errors?.api || 'Something went wrong. Please try again.';
  }

  /**
   * Gets the disclaimer error message from the config.
   * @returns {string}
   */
  get errorDisclaimerMessage() {
    return this.#config.errors?.disclaimer || 'Please accept the disclaimer to continue';
  }

  /**
   * Checks if the modal has been dismissed recently (within dismissDays).
   * @returns {boolean}
   */
  #hasBeenDismissed() {
    try {
      const dismissedAt = localStorage.getItem(STORAGE_DISMISSED_KEY);
      if (!dismissedAt) return false;

      const dismissedDate = new Date(parseInt(dismissedAt, 10));
      const now = new Date();
      const daysDiff = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

      return daysDiff < this.dismissDays;
    } catch {
      return false;
    }
  }

  /**
   * Marks the modal as dismissed.
   */
  #markAsDismissed() {
    try {
      localStorage.setItem(STORAGE_DISMISSED_KEY, Date.now().toString());
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Prefills the email input for logged-in customers.
   */
  #prefillEmail() {
    if (this.isLoggedIn && this.customerEmail) {
      this.refs.emailInput.value = this.customerEmail;
    }
  }

  /**
   * Shows the modal dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Store scroll position
    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      // Lock scroll if enabled
      if (this.lockScroll) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.#previousScrollY}px`;
      }

      dialog.showModal();

      // Focus the email input
      this.refs.emailInput.focus();

      // Add event listeners
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the modal dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Mark as dismissed when closing
    this.#markAsDismissed();

    // Remove event listeners
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    // Unlock scroll if it was locked
    if (this.lockScroll) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'auto' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // Reset form state
    this.#resetForm();
  };

  /**
   * Handles the form submission.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event?.preventDefault();

    const { emailInput, listACheckbox, listBCheckbox, submitButton, loadingIndicator, submitText } = this.refs;

    // Hide any previous errors
    this.#hideError();

    // Validate email
    if (!emailInput.value || !isValidEmail(emailInput.value)) {
      this.#showError(this.errorEmailMessage);
      emailInput.focus();
      return;
    }

    // Get selected lists
    const selectedLists = getSelectedLists(this.refs, this.listAId, this.listBId);

    // If multiple lists are available, at least one must be selected
    if ((listACheckbox || listBCheckbox) && selectedLists.length === 0) {
      this.#showError(this.errorListMessage);
      return;
    }

    // Validate disclaimer checkbox if present
    if (this.refs.disclaimerCheckbox && !this.refs.disclaimerCheckbox.checked) {
      this.#showError(this.errorDisclaimerMessage);
      return;
    }

    // Show loading state
    submitButton.disabled = true;
    submitText.hidden = true;
    loadingIndicator.hidden = false;

    try {
      // Subscribe to each selected list
      const listsToSubscribe = selectedLists.length > 0 ? selectedLists : this.listAId ? [this.listAId] : [];

      await Promise.all(
        listsToSubscribe.map((listId) => subscribeToList(emailInput.value, listId, this.companyId, this.localeData)),
      );

      this.#showSuccess();
    } catch (error) {
      console.error('Klaviyo newsletter subscription failed:', error);
      this.#showError(this.errorApiMessage);
    } finally {
      submitButton.disabled = false;
      submitText.hidden = false;
      loadingIndicator.hidden = true;
    }
  };

  /**
   * Shows an error message.
   * @param {string} message - The error message to display.
   */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    }
  }

  /**
   * Hides the error message.
   */
  #hideError() {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = '';
    }
  }

  /**
   * Shows the success state.
   */
  #showSuccess() {
    const { formContainer, successMessage } = this.refs;
    if (formContainer) formContainer.hidden = true;
    successMessage.hidden = false;

    // Auto-close after 3 seconds
    setTimeout(() => {
      this.closeDialog();
    }, 3000);
  }

  /**
   * Resets the form to its initial state.
   */
  #resetForm() {
    const { formContainer, successMessage, emailInput, listACheckbox, listBCheckbox } = this.refs;

    if (formContainer) formContainer.hidden = false;
    if (emailInput) emailInput.value = '';
    if (successMessage) successMessage.hidden = true;
    this.#hideError();

    // Reset checkboxes to their default state
    if (listACheckbox) {
      listACheckbox.checked = listACheckbox.defaultChecked;
    }
    if (listBCheckbox) {
      listBCheckbox.checked = listBCheckbox.defaultChecked;
    }
    if (this.refs.disclaimerCheckbox) {
      this.refs.disclaimerCheckbox.checked = false;
    }
  }

  /**
   * Handles click events on the modal.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Handles keydown events on the modal.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  };
}

if (!customElements.get('klaviyo-newsletter-modal')) {
  customElements.define('klaviyo-newsletter-modal', KlaviyoNewsletterModal);
}

export { KlaviyoNewsletterModal };
